/**
 * @fileoverview Shared persisted value-object schemas for the complex-inspections
 *               contract bundle (documents 11–16 of the Phase 0 audit).
 * @module import-core/contracts/complex-inspections/sharedSchemas
 *
 * Responsibilities:
 * - Define BSON/JSON-tolerant primitives (`objectIdLike`, `isoDateLike`) that
 *   accept Mongo `ObjectId`/`Date` and their HTTP/Dexie serialized forms
 *   (lowercase 24-hex strings / ISO-8601 UTC strings) without dropping fields.
 * - Define the shared value objects every containing document reuses:
 *   `LookupReference`, `LegacySource`, `PostalAddress`, audit/tombstone blocks.
 * - Own the inspection-complex category authority: `complexEligibleEntityCategorySchema`
 *   (`building | parcel` only) plus the `isComplexEligibleEntityCategory` /
 *   `isHomogeneousComplexEntityCategory` predicates every boundary shares.
 *
 * Dependencies:
 * - `zod` v3 — runtime schema validation. `mongodb` is referenced only via
 *   `import type` so the `./complex-inspections` subpath stays browser-safe.
 *
 * Architecture:
 * - Pure schema definitions. No I/O. Re-exported through the local barrel and
 *   consumed by every containing-document schema plus the sync boundary helper.
 *
 * Notes:
 * - These are persisted-boundary schemas: they intentionally accept both BSON
 *   and serialized representations. ObjectId acceptance is duck-typed
 *   (`toHexString()` returning 24-hex) rather than `instanceof` so the module
 *   has no runtime `mongodb` dependency.
 * - Writers normalize to the boundary's form; readers never lose fields
 *   through normalization.
 */

import { z } from 'zod';
import type { ObjectId } from 'mongodb';

const OBJECT_ID_HEX = /^[a-f\d]{24}$/i;

function isObjectIdLike(value: unknown): boolean {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { toHexString?: unknown }).toHexString === 'function' &&
    OBJECT_ID_HEX.test((value as { toHexString: () => string }).toHexString())
  );
}

/**
 * @contract objectIdLikeSchema
 * Purpose: Persisted ObjectId field. Accepts a BSON `ObjectId` (or ObjectId-like
 *   driver object) or its lowercase 24-hex serialized form. The value is
 *   preserved as supplied — no string coercion — so Mongo writes keep BSON ids
 *   and HTTP/Dexie writes keep strings.
 * Rules: Empty strings, non-hex strings, and other types are rejected.
 * Sync: Same schema is used at REST, sync-push, and Dexie boundaries.
 */
export const objectIdLikeSchema = z.custom<ObjectId | string>(
  (value) => typeof value === 'string' ? OBJECT_ID_HEX.test(value) : isObjectIdLike(value),
  { message: 'Expected ObjectId or 24-character hex string' },
);

const isoDatetimeStringSchema = z.string().datetime({ offset: true });

function isDateLike(value: unknown): boolean {
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  if (typeof value !== 'string') return false;
  return isoDatetimeStringSchema.safeParse(value).success;
}

/**
 * @contract isoDateLikeSchema
 * Purpose: Persisted Date field. Accepts a BSON `Date` or an ISO-8601
 *   datetime string only — arbitrary `Date.parse`-able human strings
 *   (`'Jan 5 2026'`, `'2026-01-05'`) are rejected.
 * Rules: Invalid date strings and non-date types are rejected.
 * Sync: Same schema at every boundary; Dexie stores ISO strings.
 */
export const isoDateLikeSchema = z.custom<Date | string>(isDateLike, {
  message: 'Expected Date or ISO-8601 datetime string',
});

/**
 * @contract lookupDisplaySchema / lookupReferenceSchema
 * Purpose: Canonical lookup value reference used across entities, contacts,
 *   cases, groups, and report templates (document 11 §2).
 * Fields: lookupId, lookupType, code, name, optional display presentation.
 * Rules: lookupType/code/name required; display is presentation-only.
 * Sync: Serialized with string lookupId at HTTP/Dexie boundaries.
 */
export const lookupDisplaySchema = z.object({
  textColor: z.string().optional(),
  backgroundColor: z.string().optional(),
  icon: z.string().optional(),
});

export const lookupReferenceSchema = z.object({
  lookupId: objectIdLikeSchema,
  lookupType: z.string(),
  code: z.string(),
  name: z.string(),
  display: lookupDisplaySchema.optional(),
});

/**
 * @contract legacySourceSchema
 * Purpose: Legacy provenance block carried by migrated group/entity/contact
 *   documents (document 11 §2, document 17).
 * Fields: system/table/sourceId identify the source row; optional legacyDb,
 *   departmentLegacyId, runId, importedAt, warnings.
 * Sync: Preserved verbatim across boundaries.
 */
export const legacySourceSchema = z.object({
  system: z.string(),
  table: z.string(),
  sourceId: z.string(),
  legacyDb: z.string().optional(),
  departmentLegacyId: z.number().optional(),
  runId: z.string().optional(),
  importedAt: isoDateLikeSchema.optional(),
  warnings: z.array(z.string()).optional(),
});

/**
 * @contract postalAddressSchema
 * Purpose: Canonical structured postal/mailing address shared by contacts,
 *   ownership, and delivery snapshots (document 11 §6 / document 12 §2).
 * Rules: Core lines/city/state/postalCode/countryCode required;
 *   validationStatus constrained to the documented union.
 */
export const postalAddressSchema = z.object({
  line1: z.string(),
  line2: z.string().optional(),
  city: z.string(),
  state: z.string(),
  postalCode: z.string(),
  countryCode: z.string(),
  formattedAddress: z.string().optional(),
  // Legacy ownership-mailing extras — preserved so old documents lose nothing.
  careOf: z.string().optional(),
  urbanization: z.string().optional(),
  originalAddress: z.string().optional(),
  validationStatus: z.enum(['needsValidation', 'validated', 'validationWarning']),
  validationMessage: z.string().optional(),
  validatedAt: isoDateLikeSchema.optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});

/**
 * @contract entityCategorySchema
 * Purpose: Physical discriminator for inspectable entities. An entity's
 *   physical category is never relabeled: `complex` is a genuine
 *   abstract/common-area category only — not a stand-in for a record that
 *   anchors a group (document 11 §1). Only `building` and `parcel` may
 *   anchor or join an inspection complex — see
 *   `complexEligibleEntityCategorySchema`; Sites are the separate full
 *   heterogeneous location hierarchy.
 */
export const entityCategorySchema = z.enum([
  'parcel',
  'residential',
  'building',
  'unit',
  'hydrant',
  'site',
  'complex',
]);

/**
 * @contract complexEligibleEntityCategorySchema
 * Purpose: Persisted discriminator subset identifying the ONLY entity
 *   categories that may anchor or join an inspection complex:
 *   `building | parcel`.
 * Fields: n/a — enum of category literals.
 * Rules: Sites are the separate full heterogeneous location hierarchy and are
 *   NEVER eligible as complex anchors or members; residential/unit/hydrant/
 *   complex are likewise ineligible. Applies to new writes and visit
 *   topology; legacy malformed documents remain readable where ordinary
 *   reads do not validate.
 * Sync: Same schema at REST, sync-push, Dexie, and mutation boundaries.
 */
export const complexEligibleEntityCategorySchema = z.enum(['building', 'parcel']);

export type ComplexEligibleEntityCategory = z.infer<
  typeof complexEligibleEntityCategorySchema
>;

/**
 * @process isComplexEligibleEntityCategory
 * Steps: runtime-parse `value` against `complexEligibleEntityCategorySchema`.
 * Inputs: value — any entity category candidate (unknown).
 * Outputs: true only when value is 'building' or 'parcel'; narrows the type.
 * Failure Handling: never throws — invalid values return false.
 * Idempotency: REQUIRED — pure predicate.
 */
export function isComplexEligibleEntityCategory(
  value: unknown,
): value is ComplexEligibleEntityCategory {
  return complexEligibleEntityCategorySchema.safeParse(value).success;
}

/**
 * @process isHomogeneousComplexEntityCategory
 * Steps: require the anchor category to be complex-eligible, then require the
 *   candidate category to equal it exactly.
 * Inputs: anchorCategory — the group's authoritative anchor entity category;
 *   candidateCategory — the member/transfer candidate's entity category.
 * Outputs: true only when the anchor is eligible and both categories match
 *   exactly; site→site returns false because the anchor is ineligible.
 * Failure Handling: never throws — any mismatch returns false.
 * Idempotency: REQUIRED — pure comparison.
 */
export function isHomogeneousComplexEntityCategory(
  anchorCategory: unknown,
  candidateCategory: unknown,
): boolean {
  return (
    isComplexEligibleEntityCategory(anchorCategory) &&
    candidateCategory === anchorCategory
  );
}

/**
 * @contract auditFieldsSchema / tombstoneFieldsSchema
 * Purpose: Standard version/audit/tombstone block required on every
 *   schemaVersion:1 containing document (SCHEMA-CONTRACT §10).
 * Rules: version starts at 1; createdAt/By + updatedAt/By required;
 *   `_deleted` is a required boolean on target writes.
 */
export const auditFieldsSchema = z.object({
  version: z.number().int(),
  createdAt: isoDateLikeSchema,
  createdBy: objectIdLikeSchema,
  updatedAt: isoDateLikeSchema,
  updatedBy: objectIdLikeSchema,
});

export const tombstoneFieldsSchema = z.object({
  _deleted: z.boolean(),
  deletedAt: isoDateLikeSchema.optional(),
  deletedBy: objectIdLikeSchema.optional(),
  deletionReason: z.string().optional(),
});

/**
 * @contract contactEmailSchema / contactPhoneSchema / contactAffiliationSchema
 * Purpose: Canonical contact channel arrays shared by the canonical contact
 *   document and every embedded snapshot (document 12 §2–§3).
 */
export const contactEmailSchema = z.object({
  type: lookupReferenceSchema,
  email: z.string().email(),
  isPrimary: z.boolean(),
});

export const contactPhoneSchema = z.object({
  type: lookupReferenceSchema,
  number: z.string(),
  countryCode: z.string(),
  extension: z.string().optional(),
  isPrimary: z.boolean(),
});

export const contactAffiliationSchema = z.object({
  organizationName: z.string(),
  department: z.string().optional(),
  title: z.string().optional(),
});

/**
 * @contract contactSnapshotSchema
 * Purpose: Reusable mutable contact snapshot embedded on group/entity/case
 *   links (document 12 §3). Follows canonical contact edits; never a second
 *   identity — `contactId` always references `contacts._id`.
 */
export const contactSnapshotSchema = z.object({
  displayName: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  title: z.string().optional(),
  company: z.string().optional(),
  mailingAddress: postalAddressSchema.nullable().optional(),
  address: z.string().optional(),
  emails: z.array(contactEmailSchema),
  phoneNumbers: z.array(contactPhoneSchema),
  affiliations: z.array(contactAffiliationSchema).optional(),
  canonicalUpdatedAt: isoDateLikeSchema,
});

export type LookupReferenceValue = z.infer<typeof lookupReferenceSchema>;
export type EntityCategoryValue = z.infer<typeof entityCategorySchema>;
export type PostalAddressValue = z.infer<typeof postalAddressSchema>;
export type ContactSnapshotValue = z.infer<typeof contactSnapshotSchema>;
